import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { promptsAPI } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import PromptCard from './PromptCard';
import { SkeletonList } from './Skeleton';
import './PromptList.css';

function PromptList({ currentUsername, showToast }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search input with 400ms delay
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    loadPrompts();
  }, [debouncedSearch, sort, page]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        sort,
        ...(debouncedSearch && { search: debouncedSearch }),
      };
      const response = await promptsAPI.getAll(params);
      const data = response.data;
      
      if (Array.isArray(data)) {
        setPrompts(data);
        setTotalPages(1);
      } else if (data.prompts) {
        setPrompts(data.prompts || []);
        setTotalPages(data.pagination?.total_pages || 1);
      } else {
        setPrompts([]);
      }
    } catch (err) {
      console.error('Failed to load prompts:', err);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="prompt-list-header">
        <h1>AI Prompts</h1>
        <div className="controls">
          <input
            type="text"
            placeholder="Search prompts..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="search-input"
          />
          <select value={sort} onChange={(e) => {
            setSort(e.target.value);
            setPage(1);
          }} className="sort-select">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="most_liked">Most Liked</option>
            <option value="most_disliked">Most Disliked</option>
            <option value="trending">Trending</option>
          </select>
        </div>
      </div>

      {loading ? (
        <SkeletonList count={6} />
      ) : prompts.length === 0 ? (
        <div className="no-prompts">No prompts found.</div>
      ) : (
        <>
          <div className="prompts-grid">
            {prompts.map(prompt => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                currentUsername={currentUsername}
                showToast={showToast}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PromptList;
